import { Component, ElementRef, inject, input, output, ViewChild } from '@angular/core';
import { FaceLandmarker } from '@mediapipe/tasks-vision';
import { CommonModule } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LivenessFbComponent } from "../liveness-fb/liveness-fb.component";
import { SessionService } from '../Services/session.service';
import { HeadshotUploadService } from '../headshot-upload/headshot-upload.service';
import { DialogService, DialogType } from '../Services/dialog.service';
import { ApiAboutService } from '../Services/api-about.service';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

interface DeviceMotionData {
  timestamp: number;
  gamma: number; // Side-to-side tilt
  beta: number;  // Front-to-back tilt
}

interface FaceDistanceData {
  timestamp: number;
  faceSize: number;
  landmarks: any[];
}

@Component({
    selector: 'app-liveness',
    imports: [CommonModule, MatProgressSpinnerModule, LivenessFbComponent, MatIconModule],
    templateUrl: './liveness.component.html',
    styleUrl: './liveness.component.scss'
})
export class LivenessComponent {
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;
  finalPicture = output<string>()
  livenessMetadata = output<any>() // Add new output for metadata
  private webcamRunning = false;
  drawingUtils: any
  instructions = $localize`Position your face facing the camera and then click "START."`
  
  // Challenge states
  deviceMotionChallenge = false;
  distanceChallenge = false;
  smileChallenge = false;
  
  // Challenge completion flags
  deviceMotionCompleted = false;
  distanceChangeCompleted = false;
  smileDetected = false;
  
  isDetecting = false;
  livenessCompleted = false;
  finalPhoto = "";
  
  platform = inject(Platform);
  isMobile: boolean = false;
  showLiveness = true;
  displaySpinner = false;
  showOk = false;
  
  sessionService = inject(SessionService);
  headshotService = inject(HeadshotUploadService);
  model = input.required<FaceLandmarker>();
  router = inject(Router);
  dialogService = inject(DialogService);
  aboutService = inject(ApiAboutService);
  webCameraError = false;
  cameraErrorName = "";

  // Device motion tracking
  private deviceMotionData: DeviceMotionData[] = [];
  private faceOrientationData: any[] = [];
  private motionSyncScore = 0;
  private requiredSyncScore = 2; // Reduced from 3 to 2
  private deviceMotionListener: ((event: DeviceOrientationEvent) => void) | null = null;
  private anyMotionDetected = false; // Track if any motion happened at all
  private motionTrackingStarted = false; // Track if we've started motion detection

  // Distance tracking
  private faceDistanceData: FaceDistanceData[] = [];
  private baselineFaceSize: number = 0;
  private minDistanceChange = 0.25; // Reduced to 25% for easier completion
  private maxDistanceChange = 2.0; // 200% size change max
  private distanceChangeDetected = false;
  private currentPhase: 'baseline' | 'farther' | 'closer' | 'completed' = 'baseline';
  private baselineStartTime: number = 0; // Track timing for desktop vs mobile
  private phaseStartTime: number = 0; // Track individual phase timing
  private baselineCompleted: boolean = false; // Track if baseline data collection is done

  ngOnInit() {
    window.addEventListener('popstate', this.onBackButton.bind(this));
  }

  async ngAfterViewInit() {
    this.isMobile = this.platform.ANDROID || this.platform.IOS;
    this.displaySpinner = true;
    this.isDetecting = false;
    this.startWebcam();
  }

  private async startWebcam() {
    const video = this.videoElement.nativeElement;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      video.srcObject = stream;
      video.onloadeddata = () => {
        video.play();
        this.webcamRunning = true;
        setTimeout(() => {
          this.displaySpinner = false;
        }, 1000);
        this.detectFaces();
      };
    } catch (error: any) {
      this.displaySpinner = false;
      this.webCameraError = true;
      if (error instanceof DOMException) {
        this.handleErrors(error);
      } else {
        console.error("Unexpected camera error:", error.name);
        this.handleErrors(error);
      }
    }
  }

  private async detectFaces() {
    const video = this.videoElement.nativeElement;
    const detect = async () => {
      if (!this.webcamRunning) return;
      
      const results = this.model().detectForVideo(video, performance.now());
      if (results.faceLandmarks && results.faceLandmarks.length > 0) {
        const landmarks = results.faceLandmarks[0];
        
        // Device motion challenge
        if (this.deviceMotionChallenge && !this.deviceMotionCompleted) {
          this.processDeviceMotionChallenge(landmarks);
        }
        
        // Distance challenge
        if (this.distanceChallenge && !this.distanceChangeCompleted) {
          this.processDistanceChallenge(landmarks);
        }
        
        // Final smile challenge
        if (this.smileChallenge && !this.smileDetected) {
          const smileStatus = this.inferSmileFromLandmarks(landmarks);
          if (smileStatus === 'smiling') {
            this.smileDetected = true;
            this.instructions = $localize`Perfect! Processing completed.`;
            this.showOk = true;
            await this.delay(500);
            this.finalPhoto = await this.capturePhoto();
            this.livenessCompleted = true;
          }
        }
      }

      if (!this.livenessCompleted) {
        requestAnimationFrame(detect);
      }
    };
    
    if (!this.livenessCompleted) {
      detect();
    }
  }

  async startDetection() {
    this.isDetecting = true;
    this.resetChallengeStates();
    
    if (this.isMobile) {
      await this.performDeviceChallenge();
    } else {
      await this.verifyPhysicalDistance();
    }
  }

  private resetChallengeStates() {
    this.deviceMotionChallenge = false;
    this.distanceChallenge = false;
    this.smileChallenge = false;
    this.deviceMotionCompleted = false;
    this.distanceChangeCompleted = false;
    this.smileDetected = false;
    this.motionSyncScore = 0;
    this.anyMotionDetected = false;
    this.motionTrackingStarted = false;
    this.deviceMotionData = [];
    this.faceOrientationData = [];
    this.baselineStartTime = 0;
    this.phaseStartTime = 0;
    this.baselineCompleted = false;
    this.faceDistanceData = [];
    this.baselineFaceSize = 0;
    this.currentPhase = 'baseline';
    this.distanceChangeDetected = false;
    
    //console.log('Challenge states reset');
  }

  async performDeviceChallenge(): Promise<void> {
    this.deviceMotionChallenge = true;
    this.instructions = $localize`Gently tilt your phone and turn your head - any movement counts!`;
    
    // Request device motion permission on iOS
    if (this.platform.IOS && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        if (permission !== 'granted') {
          console.warn('Device motion permission denied - skipping to distance challenge');
          await this.verifyPhysicalDistance();
          return;
        }
      } catch (error) {
        console.error('Error requesting device motion permission:', error);
        await this.verifyPhysicalDistance();
        return;
      }
    }

    // Give user 3 seconds to read the instruction before starting tracking
    setTimeout(() => {
      this.startDeviceMotionTracking();
    }, 3000);
  }

  private startDeviceMotionTracking(): void {
    this.motionTrackingStarted = true;
    this.instructions = $localize`Now start tilting! Move your phone and head together`;
    
    this.deviceMotionListener = (event: DeviceOrientationEvent) => {
      if (!this.deviceMotionChallenge || this.deviceMotionCompleted) return;
      
      const motionData: DeviceMotionData = {
        timestamp: performance.now(),
        gamma: event.gamma || 0,
        beta: event.beta || 0
      };
      
      // Track any significant motion
      if (Math.abs(motionData.gamma) > 5 || Math.abs(motionData.beta) > 5) {
        this.anyMotionDetected = true;
      }
      
      this.deviceMotionData.push(motionData);
      
      // Keep only recent data (last 5 seconds)
      const cutoffTime = performance.now() - 5000;
      this.deviceMotionData = this.deviceMotionData.filter(d => d.timestamp > cutoffTime);
    };
    
    window.addEventListener('deviceorientation', this.deviceMotionListener);
    
    // Fallback: If any motion detected after 10 seconds, complete challenge
    setTimeout(() => {
      if (!this.deviceMotionCompleted && this.anyMotionDetected) {
        //console.log('Auto-completing due to motion detection');
        this.completeDeviceMotionChallenge();
      }
    }, 10000);
    
    // Fail after 20 seconds if no motion at all
    setTimeout(() => {
      if (!this.deviceMotionCompleted) {
        if (this.anyMotionDetected) {
          //console.log('Had motion but not enough correlation - completing anyway');
          this.completeDeviceMotionChallenge();
        } else {
          this.failLivenessCheck('Device motion challenge failed - no device movement detected');
        }
      }
    }, 20000);
  }

  private processDeviceMotionChallenge(landmarks: any[]): void {
    // Don't process until motion tracking has actually started
    if (!this.motionTrackingStarted) {
      return;
    }
    
    const orientation = this.inferHeadOrientation(landmarks);
    const timestamp = performance.now();
    
    this.faceOrientationData.push({
      timestamp,
      orientation,
      landmarks
    });
    
    // Keep only recent data
    const cutoffTime = timestamp - 5000;
    this.faceOrientationData = this.faceOrientationData.filter(d => d.timestamp > cutoffTime);
    
    // Much more lenient checking
    if (this.deviceMotionData.length > 3 && this.faceOrientationData.length > 3) {
      const correlation = this.calculateMotionCorrelation();
      //console.log('Motion correlation:', correlation, 'Score:', this.motionSyncScore, 'Any motion:', this.anyMotionDetected);
      
      // Award points for any correlation at all
      if (correlation > 0.1) { // Very low threshold
        this.motionSyncScore++;
        if (this.motionSyncScore >= this.requiredSyncScore) {
          this.completeDeviceMotionChallenge();
        }
      }
      
      // Alternative completion: just having face movement + device movement
      const hasFaceMovement = this.faceOrientationData.some(f => 
        f.orientation === 'face-left' || f.orientation === 'face-right'
      );
      const hasDeviceMovement = this.anyMotionDetected;
      
      if (hasFaceMovement && hasDeviceMovement && this.deviceMotionData.length > 20) {
        //console.log('Completing due to general movement detection');
        this.motionSyncScore = this.requiredSyncScore; // Force completion
        this.completeDeviceMotionChallenge();
      }
    }
    
    // Only update instructions if we have some data to work with
    if (this.deviceMotionData.length > 5) {
      if (this.motionSyncScore > 0) {
        this.instructions = $localize`Great! Almost there. Progress: ${this.motionSyncScore}/${this.requiredSyncScore}`;
      } else if (this.anyMotionDetected) {
        this.instructions = $localize`Good! Keep tilting your device and moving your head`;
      } else {
        this.instructions = $localize`Try tilting your phone left and right while turning your head`;
      }
    }
  }

  private calculateMotionCorrelation(): number {
    if (this.deviceMotionData.length < 3 || this.faceOrientationData.length < 3) {
      return 0;
    }
    
    // Get recent device tilts and face data
    const recentDeviceData = this.deviceMotionData.slice(-10);
    const recentFaceData = this.faceOrientationData.slice(-10);
    
    let correlationScore = 0;
    let totalChecks = 0;
    
    for (const deviceData of recentDeviceData) {
      // Find face data within 1 second of device motion
      const matchingFaceData = recentFaceData.filter(faceData => 
        Math.abs(faceData.timestamp - deviceData.timestamp) < 1000
      );
      
      if (matchingFaceData.length === 0) continue;
      
      // Check if device tilt direction matches any face orientation in the time window
      const deviceTiltLeft = deviceData.gamma < -10; // Reduced threshold
      const deviceTiltRight = deviceData.gamma > 10; // Reduced threshold
      
      const hasMatchingFaceLeft = matchingFaceData.some(f => f.orientation === 'face-left');
      const hasMatchingFaceRight = matchingFaceData.some(f => f.orientation === 'face-right');
      
      totalChecks++;
      
      // Award points for any movement correlation
      if (deviceTiltLeft && hasMatchingFaceLeft) {
        correlationScore += 1;
      } else if (deviceTiltRight && hasMatchingFaceRight) {
        correlationScore += 1;
      } else if (Math.abs(deviceData.gamma) > 5) {
        // Give partial credit for any device movement with any face movement
        const hasFaceMovement = matchingFaceData.some(f => 
          f.orientation === 'face-left' || f.orientation === 'face-right'
        );
        if (hasFaceMovement) {
          correlationScore += 0.5;
        }
      }
    }
    
    //console.log('Correlation calc:', correlationScore, 'out of', totalChecks, 'checks');
    return totalChecks > 0 ? correlationScore / totalChecks : 0;
  }

  private completeDeviceMotionChallenge(): void {
    this.deviceMotionCompleted = true;
    this.deviceMotionChallenge = false;
    this.instructions = $localize`Device motion verified! Great job!`;
    this.showOk = true;
    
    if (this.deviceMotionListener) {
      window.removeEventListener('deviceorientation', this.deviceMotionListener);
      this.deviceMotionListener = null;
    }
    
    // Give user time to read success message and prepare for next challenge
    setTimeout(() => {
      this.showOk = false;
      this.instructions = $localize`Next: Distance verification. Get ready...`;
    }, 2000);
    
    setTimeout(() => {
      this.verifyPhysicalDistance();
    }, 4000); // 4 second total delay
  }

  async verifyPhysicalDistance(): Promise<void> {
    this.distanceChallenge = true;
    this.currentPhase = 'baseline';
    this.baselineStartTime = performance.now();
    this.phaseStartTime = performance.now();
    this.baselineCompleted = false;
    this.instructions = $localize`Hold still and look at the camera while we establish your baseline position`;
    
    // Start showing baseline progress after 2 seconds (gives time to read initial instruction)
    setTimeout(() => {
      if (this.currentPhase === 'baseline' && !this.baselineCompleted) {
        this.instructions = $localize`Establishing baseline... please keep your face in view`;
      }
    }, 2000);
    
    // Much longer timeout for Android compatibility
    setTimeout(() => {
      if (!this.distanceChangeCompleted) {
        console.warn('Distance challenge timeout - completing anyway if we have any baseline');
        if (this.faceDistanceData.length > 10) {
          // Force completion if we have some data
          this.baselineFaceSize = this.calculateAverageFaceSize();
          if (this.baselineFaceSize > 0) {
            this.completeDistanceChallenge();
          } else {
            this.failLivenessCheck('Unable to establish face baseline - please ensure good lighting');
          }
        } else {
          this.failLivenessCheck('Distance challenge failed - no face detected consistently');
        }
      }
    }, 60000); // 60 seconds total timeout
  }

  private processDistanceChallenge(landmarks: any[]): void {
    const faceSize = this.calculateFaceSize(landmarks);
    const timestamp = performance.now();
    
    // Only add valid face data
    if (faceSize > 0) {
      this.faceDistanceData.push({
        timestamp,
        faceSize,
        landmarks
      });
    }
    
    // Keep only recent data (last 15 seconds for more patience)
    const cutoffTime = timestamp - 15000;
    this.faceDistanceData = this.faceDistanceData.filter(d => d.timestamp > cutoffTime);
    
    switch (this.currentPhase) {
      case 'baseline':
        const elapsedTime = timestamp - this.baselineStartTime;
        const minimumBaselineTime = 4000; // 4 seconds minimum for all platforms
        const requiredFrames = this.isMobile ? 60 : 120;
        
        // Collect baseline data but don't advance until minimum time
        if (!this.baselineCompleted) {
          // Show progress only after initial instruction time
          if (elapsedTime > 2000 && this.faceDistanceData.length > 15 && this.faceDistanceData.length % 30 === 0) {
            const dataProgress = Math.min(Math.round((this.faceDistanceData.length / requiredFrames) * 100), 100);
            const timeProgress = Math.min(Math.round((elapsedTime / minimumBaselineTime) * 100), 100);
            const overallProgress = Math.max(dataProgress, timeProgress);
            this.instructions = $localize`Establishing baseline... ${overallProgress}% complete`;
          }
          
          // Mark baseline data collection as complete when we have enough data
          if (this.faceDistanceData.length >= requiredFrames && elapsedTime >= minimumBaselineTime) {
            this.baselineCompleted = true;
            this.baselineFaceSize = this.calculateAverageFaceSize();
            //console.log('Baseline data collection completed after', elapsedTime, 'ms with', this.faceDistanceData.length, 'frames');
          }
        }
        
        // Only advance to next phase after baseline is complete AND enough time has passed
        if (this.baselineCompleted && this.baselineFaceSize > 0) {
          this.currentPhase = 'farther';
          this.phaseStartTime = performance.now(); // Reset timer for next phase
          this.instructions = $localize`Baseline established! Now step back from the camera`;
        }
        
        // Safety fallback
        if (elapsedTime > 15000 && this.faceDistanceData.length > 20) {
          console.warn('Forcing baseline completion due to timeout');
          this.baselineCompleted = true;
          this.baselineFaceSize = this.calculateAverageFaceSize();
          if (this.baselineFaceSize > 0) {
            this.currentPhase = 'farther';
            this.phaseStartTime = performance.now();
            this.instructions = $localize`Baseline established! Now step back from the camera`;
          }
        }
        break;
        
      case 'farther':
        const timeInFartherPhase = timestamp - this.phaseStartTime;
        const minimumActionTime = 3000; // Give user 3 seconds to read and start moving
        
        if (this.baselineFaceSize === 0 || this.faceDistanceData.length === 0) {
          console.warn('Invalid baseline, skipping distance challenge');
          this.completeDistanceChallenge();
          return;
        }
        
        // Show preparation message first, then transition to action
        if (timeInFartherPhase < minimumActionTime) {
          const waitProgress = Math.round((timeInFartherPhase / minimumActionTime) * 100);
          this.instructions = $localize`Get ready to step back... ${waitProgress}%`;
        } else {
          // After preparation time, start checking for movement
          const reduction = (this.baselineFaceSize - faceSize) / this.baselineFaceSize;
          //console.log('Checking for step back - Reduction:', reduction, 'Current:', faceSize, 'Baseline:', this.baselineFaceSize);
          
          // Update instruction to show we're now looking for movement
          if (reduction < 0.1) {
            this.instructions = $localize`Now step back from the camera - we need to see you get smaller`;
          } else if (reduction >= this.minDistanceChange) {
            this.currentPhase = 'closer';
            this.phaseStartTime = performance.now(); // Reset timer for next phase
            this.instructions = $localize`Perfect! Now move closer to the camera for the final photo`;
            //console.log('Farther phase completed with reduction:', reduction);
          } else {
            this.instructions = $localize`Good! Keep stepping back - ${Math.round(reduction * 100)}% so far, need ${Math.round(this.minDistanceChange * 100)}%`;
          }
        }
        
        // Extended timeout for this phase
        if (timeInFartherPhase > 20000) {
          const currentReduction = (this.baselineFaceSize - faceSize) / this.baselineFaceSize;
          if (currentReduction >= 0.15) { // Accept 15% as fallback
            this.currentPhase = 'closer';
            this.phaseStartTime = performance.now();
            this.instructions = $localize`Good enough! Now move closer to the camera`;
            //console.log('Farther phase completed with fallback reduction:', currentReduction);
          } else {
            this.failLivenessCheck('Please step back from the camera as instructed');
            return;
          }
        }
        break;
        
      case 'closer':
        const timeInCloserPhase = timestamp - this.phaseStartTime;
        const minimumCloserTime = 3000; // Give user 3 seconds to read and start moving
        
        if (this.baselineFaceSize === 0) {
          console.warn('Invalid baseline, completing challenge');
          this.completeDistanceChallenge();
          return;
        }
        
        // Show preparation message first, then transition to action
        if (timeInCloserPhase < minimumCloserTime) {
          const waitProgress = Math.round((timeInCloserPhase / minimumCloserTime) * 100);
          this.instructions = $localize`Get ready to move closer... ${waitProgress}%`;
        } else {
          // After preparation time, start checking for movement
          const increase = (faceSize - this.baselineFaceSize) / this.baselineFaceSize;
          //console.log('Checking for move closer - Increase:', increase, 'Current:', faceSize, 'Baseline:', this.baselineFaceSize);
          
          // Update instruction to show we're now looking for movement
          if (increase < 0.1) {
            this.instructions = $localize`Now move closer to the camera - we need to see you get larger`;
          } else if (increase >= this.minDistanceChange) {
            this.distanceChangeDetected = true;
            this.completeDistanceChallenge();
            //console.log('Closer phase completed with increase:', increase);
          } else {
            this.instructions = $localize`Good! Keep moving closer - ${Math.round(increase * 100)}% so far, need ${Math.round(this.minDistanceChange * 100)}%`;
          }
        }
        
        // Extended timeout for this phase
        if (timeInCloserPhase > 20000) {
          const currentIncrease = (faceSize - this.baselineFaceSize) / this.baselineFaceSize;
          if (currentIncrease >= 0.15) { // Accept 15% as fallback
            this.distanceChangeDetected = true;
            this.completeDistanceChallenge();
            //console.log('Closer phase completed with fallback increase:', currentIncrease);
          } else {
            this.failLivenessCheck('Please move closer to the camera as instructed');
            return;
          }
        }
        break;
    }
  }

  private calculateFaceSize(landmarks: any[]): number {
    if (!landmarks || landmarks.length === 0) {
      console.warn('No landmarks provided for face size calculation');
      return 0;
    }
    
    try {
      // Calculate face bounding box size
      const xs = landmarks.map(p => p.x);
      const ys = landmarks.map(p => p.y);
      const width = Math.max(...xs) - Math.min(...xs);
      const height = Math.max(...ys) - Math.min(...ys);
      const faceArea = width * height;
      
      // Additional validation for Samsung Android
      if (faceArea <= 0 || !isFinite(faceArea)) {
        console.warn('Invalid face size calculated:', faceArea);
        return 0;
      }
      
      return faceArea;
    } catch (error) {
      console.error('Error calculating face size:', error);
      return 0;
    }
  }

  private calculateAverageFaceSize(): number {
    if (this.faceDistanceData.length === 0) {
      console.warn('No face distance data available for average calculation');
      return 0;
    }
    
    // Filter out invalid face sizes (0 or negative values)
    const validSizes = this.faceDistanceData
      .map(d => d.faceSize)
      .filter(size => size > 0 && isFinite(size));
      
    if (validSizes.length === 0) {
      console.warn('No valid face sizes found');
      return 0;
    }
    
    const recent = validSizes.slice(-30); // Use last 30 valid measurements
    const sum = recent.reduce((acc, size) => acc + size, 0);
    const average = sum / recent.length;
    
    //console.log('Average face size calculated:', average, 'from', recent.length, 'measurements');
    return average;
  }

  private completeDistanceChallenge(): void {
    this.distanceChangeCompleted = true;
    this.distanceChallenge = false;
    this.instructions = $localize`Distance verification complete! Perfect positioning!`;
    this.showOk = true;
    
    setTimeout(() => {
      this.showOk = false;
      this.instructions = $localize`Final step: Get ready to smile...`;
    }, 2000);
    
    setTimeout(() => {
      this.startSmileChallenge();
    }, 3500); // 3.5 second delay before smile challenge
  }

  private startSmileChallenge(): void {
    this.smileChallenge = true;
    this.instructions = $localize`Perfect! Now smile for your final photo!`;
    
    // Fail after 15 seconds if no smile detected
    setTimeout(() => {
      if (!this.smileDetected) {
        this.failLivenessCheck('Smile challenge failed - no smile detected');
      }
    }, 15000);
  }

  private failLivenessCheck(reason: string): void {
    console.warn('Liveness check failed:', reason);
    this.instructions = $localize`Liveness verification failed. Please try again.`;
    this.showOk = false;
    this.isDetecting = false;
    this.livenessCompleted = false;
    
    // Show retry button or redirect
    setTimeout(() => {
      this.resetChallengeStates();
      this.instructions = $localize`Please click START to try again.`;
    }, 3000);
  }

  // Keep existing utility methods
  inferHeadOrientation(landmarks: any[]): string {
    const video = this.videoElement.nativeElement;
    const width = video.videoWidth;
    const height = video.videoHeight;

    const mouth = landmarks[13];
    const leftEar = landmarks[234];
    const rightEar = landmarks[454];

    if (!mouth || !leftEar || !rightEar) {
      return '';
    }

    const normalizedMouth = this.normalize(mouth, width, height);
    const normalizedLeftEar = this.normalize(leftEar, width, height);
    const normalizedRightEar = this.normalize(rightEar, width, height);

    const distanceLeft = Math.sqrt(
      Math.pow(normalizedMouth.x - normalizedLeftEar.x, 2) +
      Math.pow(normalizedMouth.y - normalizedLeftEar.y, 2) +
      Math.pow(normalizedMouth.z - normalizedLeftEar.z, 2)
    );

    const distanceRight = Math.sqrt(
      Math.pow(normalizedMouth.x - normalizedRightEar.x, 2) +
      Math.pow(normalizedMouth.y - normalizedRightEar.y, 2) +
      Math.pow(normalizedMouth.z - normalizedRightEar.z, 2)
    );

    const zDifference = normalizedLeftEar.z - normalizedRightEar.z;

    let distanceThreshold = 0.03;
    let zLeftThreshold = -0.05;
    let zRightThreshold = 0.05;

    if (!this.isMobile) {
      distanceThreshold = 0.03;
      zLeftThreshold = -0.04;
      zRightThreshold = 0.04;
    }

    if (Math.abs(distanceLeft - distanceRight) < distanceThreshold && Math.abs(zDifference) < distanceThreshold) {
      return "face-front";
    } else if (Math.abs(distanceLeft - distanceRight) >= distanceThreshold && zDifference < zLeftThreshold) {
      return "face-left";
    } else if (Math.abs(distanceLeft - distanceRight) >= distanceThreshold && zDifference > zRightThreshold) {
      return "face-right";
    } else {
      return "";
    }
  }

  private normalize(landmark: any, width: number, height: number) {
    return {
      x: landmark.x / width,
      y: landmark.y / height,
      z: landmark.z
    };
  }

  inferSmileFromLandmarks(landmarks: any[]): string {
    const leftMouthCorner = landmarks[61];
    const rightMouthCorner = landmarks[291];
    const upperLip = landmarks[13];
    const lowerLip = landmarks[14];

    const horizontalMouthDistance = Math.sqrt(
      Math.pow(rightMouthCorner.x - leftMouthCorner.x, 2) +
      Math.pow(rightMouthCorner.y - leftMouthCorner.y, 2) +
      Math.pow(rightMouthCorner.z - leftMouthCorner.z, 2)
    );

    const verticalMouthDistance = Math.sqrt(
      Math.pow(upperLip.x - lowerLip.x, 2) +
      Math.pow(upperLip.y - lowerLip.y, 2) +
      Math.pow(upperLip.z - lowerLip.z, 2)
    );

    const horizontalThreshold = 0.05;
    const verticalThreshold = 0.02;
    if (horizontalMouthDistance > horizontalThreshold && verticalMouthDistance > verticalThreshold) {
      return "smiling";
    } else {
      return "not-smiling";
    }
  }

  activateManualMode(event: MouseEvent) {
    event.preventDefault();
    this.showLiveness = false;
    this.stopCamera();
  }

  async capturePhoto(): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const video = this.videoElement.nativeElement;
        
        // Validate video is ready
        if (!video.videoWidth || !video.videoHeight) {
          reject(new Error('Video not ready for capture'));
          return;
        }
        
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
        const imageData = canvas.toDataURL('image/jpeg');
        canvas.remove();
        
        // Validate the base64 string
        if (!imageData || imageData.length < 100 || !imageData.startsWith('data:image')) {
          reject(new Error('Failed to capture valid image'));
          return;
        }
        
        resolve(imageData);
      } catch (error) {
        reject(error);
      }
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  submitPhoto() {
    // Include challenge completion metadata
    const metadata = {
      deviceMotionCompleted: this.deviceMotionCompleted,
      distanceChangeCompleted: this.distanceChangeCompleted,
      motionSyncScore: this.motionSyncScore,
      distanceChangeDetected: this.distanceChangeDetected,
      isMobile: this.isMobile,
      timestamp: new Date().toISOString()
    };
    
    // Debug logging
    //console.log('Submitting photo, length:', this.finalPhoto.length);
    //console.log('Photo prefix:', this.finalPhoto.substring(0, 50));
    
    // Emit metadata separately
    this.livenessMetadata.emit(metadata);
    
    // Emit the base64 image string as expected
    this.finalPicture.emit(this.finalPhoto);
  }

  ngOnDestroy() {
    if (this.showLiveness) {
      this.stopCamera();
    }
    if (this.deviceMotionListener) {
      window.removeEventListener('deviceorientation', this.deviceMotionListener);
    }
  }

  stopCamera() {
    if (this.videoElement) {
      const video = this.videoElement.nativeElement;
      const stream = video.srcObject as MediaStream;
      stream?.getTracks().forEach(track => track.stop());
    }
    this.webcamRunning = false;
  }

  processImage(image: string) {
    if (image) {
      this.finalPicture.emit(image);
    }
  }

  back() {
    this.headshotService.showTips = true;
  }

  onBackButton(): void {
    this.back();
  }

  handleErrors(error: any): void {
    const messages: { [key: string]: { title: string; description: string } } = {
      NotFoundError: {
        title: $localize`No cameras found`,
        description: $localize`We could not detect any cameras connected to your device. Please ensure a camera is properly connected and try again.`,
      },
      NotAllowedError: {
        title: $localize`Permission to access the camera was denied.`,
        description: $localize`You have permanently denied access to the camera. Please enable camera permissions manually in your browser settings and try again.`,
      },
      PlayError: {
        title: $localize`Failed to play video`,
        description: $localize`An error occurred while trying to play the camera stream. Please try again.`,
      },
      NotSupportedError: {
        title: $localize`Camera not supported`,
        description: $localize`Your browser does not support camera access. Please use a modern browser like Chrome, Firefox, or Safari.`,
      },
    };
    this.webCameraError = true;
    this.cameraErrorName = error.name;
  }

  retry() {
    this.webcamRunning = false;
    this.webCameraError = false;
    this.resetChallengeStates();
    setTimeout(() => {
      this.stopCamera();
      this.startWebcam();
    });
  }

  exitApp() {
    this.sessionService.endSession();
  }
}